import { useEffect, type ReactNode } from "react";
import { ButtonLink, Card, ColorDot, Grid, Icon, LanguageSwitcher, useT, type MessageKey } from "@mmm/ui";
import { appEntryUrl, type AuthIntent } from "./appOrigin.js";
import footerLinks from "./footerLinks.json";
import socialLinks from "./socialLinks.json";
import { useBackendReadiness } from "./useBackendReadiness.js";

export function LandingScreen() {
  const healthStatus = useBackendReadiness();
  const t = useT();

  useEffect(() => {
    const previousTitle = document.title;
    const metadata = [
      ['meta[name="description"]', "description"],
      ['meta[property="og:title"]', "og:title"],
      ['meta[property="og:description"]', "og:description"],
      ['meta[name="twitter:title"]', "twitter:title"],
      ['meta[name="twitter:description"]', "twitter:description"],
      ['meta[name="robots"]', "robots"],
    ] as const;
    const previousMetadata = metadata.map(([selector]) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      return { element, content: element?.content };
    });
    for (let index = 0; index < metadata.length; index++) {
      let element = previousMetadata[index].element;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(metadata[index][1].startsWith("og:") ? "property" : "name", metadata[index][1]);
        document.head.append(element);
        previousMetadata[index].element = element;
      }
      element.content = index === 5
        ? "index, follow"
        : index === 0 || index === 2 || index === 4
          ? t("landing.heroBody")
          : t("landing.metaTitle");
    }
    document.title = t("landing.metaTitle");
    return () => {
      document.title = previousTitle;
      for (let index = 0; index < previousMetadata.length; index++) {
        const previous = previousMetadata[index];
        if (previous.content === undefined) previous.element?.remove();
        else if (previous.element) previous.element.content = previous.content;
      }
    };
  }, [t]);

  const healthLabel = {
    checking: t("landing.statusChecking"),
    ready: t("landing.statusReady"),
    unavailable: t("landing.statusUnavailable"),
  }[healthStatus];

  return (
    <div className="mmm-landing">
      <a className="mmm-landing-skip" href="#main-content">
        {t("landing.skipToContent")}
      </a>

      <header className="mmm-landing-header">
        <a className="mmm-landing-brand" href="#main-content" aria-label={t("landing.backToTop")}>
          <img src="/favicon.svg" alt="" />
          <span>
            {t("brand.map")} {t("brand.my")} <strong>{t("brand.mind")}</strong>
          </span>
        </a>

        <nav className="mmm-landing-nav" aria-label={t("landing.navigation")}>
          <a href="#how-it-works">{t("landing.howItWorksNav")}</a>
          <a href="#product">{t("landing.productNav")}</a>
        </nav>

        <div className="mmm-landing-auth" aria-label={t("auth.actions")}>
          <span
            className="mmm-landing-header-health"
            role="status"
            aria-live="polite"
            aria-label={healthLabel}
            title={healthLabel}
          >
            <ColorDot
              color="currentColor"
              size="xs"
              className={`mmm-landing-health-dot is-${healthStatus}`}
            />
          </span>
          <LanguageSwitcher />
          <LandingAuthAction intent="signIn" variant="text">
            {t("auth.signIn")}
          </LandingAuthAction>
          <LandingAuthAction intent="signUp" variant="primary">
            {t("landing.startStudying")}
          </LandingAuthAction>
        </div>
      </header>

      <main id="main-content">
        <section className="mmm-landing-hero">
          <div className="mmm-landing-orb mmm-landing-orb--one" />
          <div className="mmm-landing-orb mmm-landing-orb--two" />

          <div className="mmm-landing-hero-copy">
            <p className="mmm-landing-eyebrow">{t("landing.eyebrow")}</p>
            <h1>{t("landing.heroTitle")}</h1>
            <p className="mmm-landing-hero-body">{t("landing.heroBody")}</p>

            <div className="mmm-landing-hero-actions">
              <LandingAuthAction intent="signUp" variant="primary">
                {t("landing.startStudying")}
                <span aria-hidden="true">→</span>
              </LandingAuthAction>
              <ButtonLink variant="secondary" href="#product">
                <Icon name="map" />
                {t("landing.exploreProduct")}
              </ButtonLink>
            </div>


            <div className="mmm-landing-benefits" aria-label={t("landing.benefitsLabel")}>
              <span>
                <Icon name="map" />
                {t("landing.benefitMaps")}
              </span>
              <span>
                <Icon name="calendar" />
                {t("landing.benefitPlanning")}
              </span>
              <span>
                <Icon name="ai" />
                {t("landing.benefitPractice")}
              </span>
            </div>
          </div>

          <MindMapPreview hero />
        </section>

        <section id="how-it-works" className="mmm-landing-section mmm-landing-steps">
          <SectionHeading
            eyebrow={t("landing.stepsEyebrow")}
            title={t("landing.stepsTitle")}
            body={t("landing.stepsBody")}
          />

          <Grid minItemWidth={230} gap={18} className="mmm-landing-step-grid">
            <StepCard number="01" icon="add" title={t("landing.stepOneTitle")} body={t("landing.stepOneBody")} />
            <StepCard number="02" icon="connect" title={t("landing.stepTwoTitle")} body={t("landing.stepTwoBody")} />
            <StepCard number="03" icon="ai" title={t("landing.stepThreeTitle")} body={t("landing.stepThreeBody")} />
          </Grid>
          <Card padding="md" className="mmm-landing-language-card">
            <div className="mmm-landing-feature-icon is-coral"><Icon name="logo" /></div>
            <div><h3>{t("landing.languageBadge")}</h3><p>{t("landing.languageBody")}</p></div>
          </Card>
        </section>

        <section id="product" className="mmm-landing-section mmm-landing-product">
          <SectionHeading
            eyebrow={t("landing.workspaceEyebrow")}
            title={t("landing.featureTitle")}
            body={t("landing.featureBody")}
          />

          <div className="mmm-landing-showcase">
            <article className="mmm-landing-showcase-copy">
              <span className="mmm-landing-number">01</span>
              <h3>{t("landing.practiceTitle")}</h3>
              <p>{t("landing.practiceBody")}</p>
              <ul>
                <li>
                  <span aria-hidden="true">✓</span>
                  {t("landing.practicePointOne")}
                </li>
                <li>
                  <span aria-hidden="true">✓</span>
                  {t("landing.practicePointTwo")}
                </li>
              </ul>
            </article>
            <TestPreview />
            <article className="mmm-landing-showcase-copy">
              <span className="mmm-landing-number">02</span>
              <h3>{t("landing.planningTitle")}</h3>
              <p>{t("landing.planningBody")}</p>
              <ul>
                <li><span aria-hidden="true">✓</span>{t("landing.planningPointOne")}</li>
                <li><span aria-hidden="true">✓</span>{t("landing.planningPointTwo")}</li>
              </ul>
            </article>
            <PlannerPreview />
          </div>
        </section>

        <section id="ai-data" className="mmm-landing-section mmm-landing-trust">
          <SectionHeading
            eyebrow={t("landing.dataUseEyebrow")}
            title={t("landing.dataUseTitle")}
            body={t("landing.dataUseBody")}
          />
          <a className="mmm-landing-trust-link" href="https://learn.microsoft.com/en-us/azure/foundry/responsible-ai/openai/data-privacy" target="_blank" rel="noreferrer">{t("landing.dataUseLink")}</a>
        </section>

        <section className="mmm-landing-section mmm-landing-final">
          <Card padding="lg" className="mmm-landing-final-card">
            <div>
              <p className="mmm-landing-eyebrow">{t("landing.ctaEyebrow")}</p>
              <h2>{t("landing.ctaTitle")}</h2>
              <p>{t("landing.ctaBody")}</p>
            </div>
            <LandingAuthAction intent="signUp" variant="gold">
              {t("landing.startStudying")}
              <span aria-hidden="true">→</span>
            </LandingAuthAction>
          </Card>
        </section>
      </main>

      <footer className="mmm-landing-footer">
        <a className="mmm-landing-brand" href="#main-content" aria-label={t("landing.backToTop")}>
          <img src="/favicon.svg" alt="" />
          <span>
            {t("brand.map")} {t("brand.my")} <strong>{t("brand.mind")}</strong>
          </span>
        </a>
        <p>{t("landing.footerTagline")}</p>
        <nav className="mmm-landing-footer-nav" aria-label={t("footer.navigation")}>
          {footerLinks.map((item) => (
            <a key={item.path} href={item.path}>
              {t(item.labelKey as MessageKey)}
            </a>
          ))}
          {socialLinks.map((item) => {
            const label = t(item.labelKey as MessageKey);
            return (
              <a
                key={item.url}
                className="mmm-landing-social-link"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                  <path fill="currentColor" d={item.iconPath} />
                </svg>
              </a>
            );
          })}
        </nav>
      </footer>
    </div>
  );
}

function LandingAuthAction({
  intent,
  variant,
  children,
}: {
  intent: AuthIntent;
  variant: "text" | "primary" | "gold";
  children: ReactNode;
}) {
  return (
    <ButtonLink href={appEntryUrl(intent)} variant={variant}>
      {children}
    </ButtonLink>
  );
}

function SectionHeading({ eyebrow, title, body }: { eyebrow: string; title: string; body: string }) {
  return (
    <div className="mmm-landing-section-heading">
      <p className="mmm-landing-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{body}</p>
    </div>
  );
}

function StepCard({
  number,
  icon,
  title,
  body,
}: {
  number: string;
  icon: string;
  title: string;
  body: string;
}) {
  return (
    <Card padding="md" className="mmm-landing-step-card">
      <div className="mmm-landing-step-top">
        <span>{number}</span>
        <div className="mmm-landing-step-icon">
          <Icon name={icon} />
        </div>
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
    </Card>
  );
}

function PreviewChrome({ children }: { children: React.ReactNode }) {
  const t = useT();

  return (
    <>
      <div className="mmm-landing-window-bar">
        <div className="mmm-landing-window-dots" aria-hidden="true">
          <ColorDot color="#c44820" size="xs" />
          <ColorDot color="var(--mmm-gold)" size="xs" />
          <ColorDot color="var(--mmm-success)" size="xs" />
        </div>
        <div className="mmm-landing-address">
          <span aria-hidden="true">⌁</span>
          {t("landing.previewAddress")}
        </div>
        <div className="mmm-landing-window-spacer" />
      </div>
      {children}
    </>
  );
}

function MindMapPreview({ hero = false }: { hero?: boolean }) {
  const t = useT();

  return (
    <figure className="mmm-landing-preview-wrap">
      <Card padding="none" className={`mmm-landing-preview ${hero ? "mmm-landing-preview--hero" : ""}`.trim()}>
        <PreviewChrome>
          <img
            className="mmm-landing-product-image"
            src="/mind-map-notes.png"
            alt={t("landing.previewMindMap")}
            width={1352}
            height={1230}
            loading={hero ? "eager" : "lazy"}
            fetchPriority={hero ? "high" : "auto"}
          />
        </PreviewChrome>
      </Card>
      <figcaption>{t("landing.previewMindMap")}</figcaption>
    </figure>
  );
}

function PlannerPreview() {
  const t = useT();

  return (
    <figure className="mmm-landing-preview-wrap">
      <Card padding="none" className="mmm-landing-preview">
        <PreviewChrome>
          <img
            className="mmm-landing-product-image"
            src="/planning-workspace.png"
            alt={t("landing.previewPlanner")}
            width={1352}
            height={756}
            loading="lazy"
          />
        </PreviewChrome>
      </Card>
      <figcaption>{t("landing.previewPlanner")}</figcaption>
    </figure>
  );
}

function TestPreview({ hero = false }: { hero?: boolean }) {
  const t = useT();

  return (
    <figure className="mmm-landing-preview-wrap">
      <Card padding="none" className={`mmm-landing-preview ${hero ? "mmm-landing-preview--hero" : ""}`.trim()}>
        <PreviewChrome>
          <img
            className="mmm-landing-product-image"
            src="/answer-review-clean.png"
            alt={t("landing.previewTest")}
            width={1635}
            height={960}
            loading={hero ? "eager" : "lazy"}
            fetchPriority={hero ? "high" : "auto"}
          />
        </PreviewChrome>
      </Card>
      <figcaption>{t("landing.previewTest")}</figcaption>
    </figure>
  );
}
